import { Eye, Info, SealCheck, Spinner, XCircle } from "@phosphor-icons/react";
import { Button, Checkbox, DatePicker, Form, FormItemProps, Input, Radio, Select, Space, Tooltip, Upload } from "antd";
import { AxiosError } from "axios";
import DocPreview from "components/DocPreview";
import Overlay from "components/Overlay";
import { MIXPANEL_EVENTS } from "constants/mixpanelEvents";
import { useApp } from "context/AppContext";
import dayjs from "dayjs";
import { useQuestionnaire } from "pages/Questionnaire";
import { postAnswer } from "pages/Questionnaire/Api";
import { getDocumentsRequiredIcon } from "pages/Questionnaire/constant";
import React, { memo, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Mixpanel from "utils/mixpanel";
import { clsx, useBoolean } from "x-wings";
import "./styles.scss";

type QuestionnaireFormItemProps = PopulatedQuestion & { answer: TAnswer | undefined } & {
	index: number;
	handleOpenCorrectAndWrongWayModal: (question: PopulatedQuestion) => void;
};

const QuestionnaireFormItem: React.FC<QuestionnaireFormItemProps> = (props) => {
	const { applicationId } = useApp();
	const DocIdForUSA = "68d4295ddb931e1e3dd80c69"
	const { updateAnswerByVisaQuestionId } = useQuestionnaire();
	const { orderId } = useParams();
	const [isPosting, setIsPosting] = useState(false);
	const [validateStatus, setValidateStatus] = useState<FormItemProps["validateStatus"]>();
	const [postingProgress, setPostingProgress] = useState(0);
	const [file, setFile] = useState<File>();

	const status = props.answer?.status;
	const ref = useRef(null);

	const { value: isPreviewOpen, setTrue: onPreviewOpen, setFalse: onPreviewClose } = useBoolean(false);

	useEffect(() => {
		if (status === "REJECTED") {
			setValidateStatus("error");
		}
	}, [status]);

	const handlePostAnswer = async (
		answer: string,
		file?: File | Blob | string,
		onProgress?: ((event: UploadProgressEvent) => void) | undefined
	) => {
		if (!(answer || file)) return;
		if (!orderId || !applicationId) return;
		try {
			setIsPosting(true);
			setValidateStatus(undefined);
			const { data } = await postAnswer(
				orderId,
				applicationId,
				{
					visa_questions_id: props._id,
					traveller_id: applicationId,
					answer: answer,
					file: file
				},
				onProgress
			);
			setValidateStatus("success");
			updateAnswerByVisaQuestionId(props._id, data.data);
			const event = file
				? MIXPANEL_EVENTS.QUESTIONNAIRE.DOCUMENT_UPLOADED
				: MIXPANEL_EVENTS.QUESTIONNAIRE.ANSWER_SUBMITTED;
			Mixpanel.track(event, {
				orderId,
				applicationId,
				questionId: props._id,
				questionType: props.type,
				question: props.question
			});
		} catch (error) {
			setValidateStatus("error");
			console.error(error);
		} finally {
			setIsPosting(false);
		}
	};

	const handleShowDocumentPreview: React.MouseEventHandler<HTMLElement> = (event) => {
		event.stopPropagation();
		onPreviewOpen();
	};

	return (
		<div className="relative" ref={ref} data-question-no={props.index} data-question-id={props._id}>
			{status === "APPROVED" && (
				<Tooltip
					title="The answer of the question is approved by the visa expert. You can't change the answer."
					className="absolute right-1 top-1 z-10 text-emerald-600">
					<SealCheck size={16} weight="fill" />
				</Tooltip>
			)}
			{status === "REJECTED" && (
				<Tooltip
					title="The answer of the question is rejected by the visa expert. Please resolve the issue with answer."
					className="absolute right-1 top-1 z-10 text-rose-600">
					<XCircle size={16} weight="fill" />
				</Tooltip>
			)}
			<Form.Item
				className="mb-6"
				colon={false}
				name={props.type === "MULTIPLE_CHOICE" ? undefined : props._id}
				label={
					props.type === "DOCUMENT" ? undefined : (
						<div className="w-full flex items-center justify-between font-medium">{props.question}</div>
					)
				}
				preserve
				// required={props.is_required} //TODO: Uncomment this line
				help={status === "REJECTED" && props.answer?.issue_reason}
				validateStatus={validateStatus}>
				{props.type === "TEXTAREA" && (
					<Input.TextArea
						disabled={status === "APPROVED"}
						placeholder="Type here..."
						autoSize={{ minRows: 2, maxRows: 5 }}
						onBlur={(event) => handlePostAnswer(event.currentTarget.value)}
						defaultValue={props.answer?.answer[0]}
					/>
				)}
				{props.type === "TEXT" && (
					<Input
						disabled={status === "APPROVED"}
						placeholder="Enter here"
						defaultValue={props.answer?.answer[0]}
						onBlur={(event) => handlePostAnswer(event.currentTarget.value)}
					/>
				)}
				{props.type === "CALENDAR" && (
					<DatePicker
						disabled={status === "APPROVED"}
						className="w-full"
						size="large"
						format="DD/MM/YYYY"
						placeholder="DD/MM/YYYY"
						defaultValue={props.answer?.answer[0] ? dayjs(props.answer?.answer[0]) : undefined}
						onChange={(date) => handlePostAnswer(date.format("MM/DD/YYYY"))}
					/>
				)}
				{props.type === "SINGLE_CHOICE" && props.options.length <= 10 && (
					<Radio.Group
						disabled={status === "APPROVED"}
						defaultValue={props.answer?.answer[0]}
						onChange={(event) => handlePostAnswer(event.target.value)}>
						<Space direction="vertical">
							{props.options.map((val, index) => {
								return (
									<Radio key={index} value={val}>
										{val}
									</Radio>
								);
							})}
						</Space>
					</Radio.Group>
				)}
				{props.type === "MULTIPLE_CHOICE" && (
					<Checkbox.Group
						disabled={status === "APPROVED"}
						defaultValue={props.answer?.answer[0]?.split(",").map((item) => item.trim()) || []}
						onChange={(val) => handlePostAnswer(val.join(","))}>
						<Space direction="vertical">
							{props.options.map((val, index) => (
								<Checkbox key={index} value={val}>
									{val}
								</Checkbox>
							))}
						</Space>
					</Checkbox.Group>
				)}
				{(props.type === "DROPDOWN" || (props.type === "SINGLE_CHOICE" && props.options.length > 10)) && (
					<Select
						disabled={status === "APPROVED"}
						defaultValue={props.answer?.answer[0]}
						placeholder="Select option"
						options={props.options.map((val) => ({ label: val, value: val }))}
						size="large"
						showSearch
						onChange={(value) => handlePostAnswer(value)}
					/>
				)}

				{props.type === "DOCUMENT" && (
					<Upload
						disabled={status === "APPROVED"}
						className="block"
						showUploadList={false}
						maxCount={1}
						beforeUpload={(file) => setFile(file)}
						customRequest={async ({ file, onProgress, onError, onSuccess }) => {
							const handleOnProgress: typeof onProgress = (event) => {
								setPostingProgress(event.percent ?? 0);
								onProgress?.(event);
							};
							try {
								await handlePostAnswer("", file, handleOnProgress);
								onSuccess?.("Uploaded");
							} catch (error) {
								if (error instanceof AxiosError) {
									onError?.({
										status: parseInt(error.code ?? "422"),
										method: "POST",
										url: error.config?.url ?? "localhost",
										...error
									});
								}
							}
						}}>
						<div
							data-isUploaded={!!props.answer?.assets_id?.[0]?.doc_id}
							aria-label="documents-requirements-list-item"
							className={clsx(
								"p-3 w-56 group relative rounded-lg ring-1  cursor-pointer flex-col justify-start items-start gap-3 inline-flex overflow-hidden",
								props.answer?.assets_id?.[0]?.doc_id
									? "bg-emerald-100 ring-emerald-600"
									: "bg-white hover:bg-primary-50 hover:ring-primary-600 ring-gray-300"
							)}>
							{isPosting ? (
								<span className="absolute end-3 top-3 cursor-pointer grid place-items-center text-primary-600">
									<Spinner size={18} className="animate-spin" />
								</span>
							) : (
								<span
									role="button"
									onClick={(e) => {
										const { answer, handleOpenCorrectAndWrongWayModal, ...question } = props;
										e.stopPropagation();
										props.handleOpenCorrectAndWrongWayModal(question);
									}}
									className="absolute end-3 top-3 text-gray-500 cursor-pointer grid place-items-center hover:text-primary-600 group-hover:visible invisible">
									<Info size={18} />
								</span>
							)}

							{props.answer?.assets_id[0]?.doc_id && props._id !== DocIdForUSA && (
								<span
									onClick={handleShowDocumentPreview}
									className="grid place-items-center gap-1 absolute  end-3 bottom-3 cursor-pointer text-primary-600 hover:bg-white size-6 hover:ring-1 ring-gray-300 rounded-sm">
									<Eye size={18} />
								</span>
							)}

							<div className="flex items-center justify-between w-full">
								<img
									src={getDocumentsRequiredIcon(props.icon)}
									alt={props.icon ?? props.doc_category ?? "Generic Doc"}
									width={24}
									height={24}
									className="size-6 max-h-6 max-w-6"
								/>
								{props._id === DocIdForUSA && !props.answer?.assets_id?.[0]?.doc_id && (
  <Button
    onClick={(e) => {
      e.stopPropagation();

      const link = document.createElement("a");
      link.href = "https://smv-public.stampmyvisa.com/important-docs/DS-160-Simplified.xlsx";
      link.setAttribute("download", "DS-160-Simplified.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }}
  >
    Download
  </Button>
)}

							</div>
							<p className="text-[13px] font-medium leading-4 tracking-[-0.04px] text-gray-800">
								{props.doc_category}
								{props.short_description && (
									<p className="mt-1 text-[12px] leading-[15px] tracking-[0.01px] text-gray-600">
										{props.short_description}
									</p>
								)}
							</p>
						</div>
					</Upload>
				)}
			</Form.Item>
			{props.answer?.assets_id[0]?.doc_id && (
				<Overlay open={isPreviewOpen} onClose={onPreviewClose}>
					<DocPreview doc_id={props.answer?.assets_id[0]?.doc_id!} />
				</Overlay>
			)}
		</div>
	);
};

export default memo(QuestionnaireFormItem);

